/**
 * GenerateProposalStep — wizard step 9 active surface (Phase G4B CP2).
 *
 * Replaces the passive honest-gate placeholder from G4A CP2. Full
 * behaviour:
 *   1. On mount: `GET /api/proposals/generation/budget` to render
 *      the "X of 110 remaining" indicator.
 *   2. Generate button: enabled when `remaining >= 11`; disabled
 *      with inline copy + `next_reset_at` when below.
 *   3. Click → POST /api/proposals/{id}/generate → spinner + honest
 *      subtitle ("Generating your proposal — this takes about a
 *      minute"). No retry loop.
 *   4. Response handling:
 *        200 ProposalPublic → navigate to `/proposals/{id}/draft`
 *        200 `{code: "llm_disabled"}` → inline banner (no retry)
 *        402 `generation_budget_exhausted` → inline banner with
 *          next_reset_at
 *        402 `proposals_premium_only` → inline PremiumUpgradePanel
 *        409 `generation_in_progress` → inline banner with
 *          lock_expires_at
 *        network error → generic inline banner
 *
 * Voice discipline: every user-visible string is factual + neutral.
 * No banned phrases (verified via CP2 voice sweep).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  ShieldAlert,
  Info,
  Ban,
  Clock,
  RefreshCw,
} from "lucide-react";
import AIDisclosureBadge from "@/components/AIDisclosureBadge";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import FeatureDisabled from "@/feature-flags/FeatureDisabled";
import { api, formatApiError } from "@/api/client";

const GENERATE_TIMEOUT_MS = 120_000; // 120s — sequential 11 sections × 5-8s each

function formatResetDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}


export default function GenerateProposalStep({ proposalId }) {
  const navigate = useNavigate();
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [budgetPremiumUpsell, setBudgetPremiumUpsell] = useState(false);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  // banner: {kind, title, body, subline?}
  const [banner, setBanner] = useState(null);

  const fetchBudget = useCallback(async () => {
    setBudgetLoading(true);
    try {
      const res = await api.get("/proposals/generation/budget");
      setBudget(res.data);
      setBudgetPremiumUpsell(false);
      setFeatureDisabled(false);
    } catch (err) {
      // Discriminate by `code` — premium vs module-disabled require
      // different inline UX. G4B CP2 rerun P0 fix.
      const code = err?.response?.data?.detail?.code;
      if (code === "proposals_premium_only") {
        setBudgetPremiumUpsell(true);
        return;
      }
      if (code === "feature_disabled") {
        setFeatureDisabled(true);
        return;
      }
      setBanner({
        kind: "error",
        title: "Could not load your generation budget",
        body: formatApiError(err),
      });
    } finally {
      setBudgetLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBudget();
  }, [fetchBudget]);

  async function handleGenerate() {
    if (!proposalId) return;
    setBanner(null);
    setGenerating(true);
    try {
      const res = await api.post(
        `/proposals/${proposalId}/generate`,
        {},
        { timeout: GENERATE_TIMEOUT_MS },
      );
      // Kill-switch: server returned 200 with { code: "llm_disabled" }.
      if (res.data && res.data.code === "llm_disabled") {
        setBanner({
          kind: "warning",
          title: "AI section generation is temporarily unavailable",
          body:
            res.data.message ||
            "Please try again later. Your wizard data is preserved.",
        });
        return;
      }
      // Success — navigate to the draft view.
      navigate(`/proposals/${proposalId}/draft`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || {};
      if (status === 402 && detail.code === "generation_budget_exhausted") {
        const reset = formatResetDate(detail.next_reset_at);
        setBanner({
          kind: "warning",
          title: "Generation budget is fully used",
          body:
            `You have used ${detail.cap - detail.remaining} of your ${detail.cap} ` +
            `section-generations in the last ${detail.window_days} days. ` +
            (reset ? `The next slot opens on ${reset}.` : ""),
        });
        fetchBudget();  // Refresh the indicator.
        return;
      }
      if (detail.code === "proposals_premium_only") {
        setBudgetPremiumUpsell(true);
        return;
      }
      if (detail.code === "feature_disabled") {
        setFeatureDisabled(true);
        return;
      }
      if (status === 409 && detail.code === "generation_in_progress") {
        const expires = formatResetDate(detail.lock_expires_at);
        setBanner({
          kind: "warning",
          title: "Another generation is already running for this proposal",
          body:
            "Please wait for it to complete. " +
            (expires
              ? `The soft-lock automatically releases by ${expires}.`
              : "The soft-lock automatically releases after five minutes."),
        });
        return;
      }
      setBanner({
        kind: "error",
        title: "Something went wrong while generating",
        body: formatApiError(err),
      });
    } finally {
      setGenerating(false);
    }
  }

  if (featureDisabled) {
    return (
      <div data-testid="wizard-step-generate">
        <FeatureDisabled
          testId="grant-writer-feature-disabled"
          title="Grant Writer is temporarily disabled"
          message="An administrator has paused the Grant Writer module. Your saved proposal drafts remain in the system and will re-appear when the feature is re-enabled."
        />
      </div>
    );
  }

  if (budgetPremiumUpsell) {
    return (
      <div data-testid="wizard-step-generate">
        <PremiumUpgradePanel scope="proposals" />
      </div>
    );
  }

  const remaining = budget?.remaining ?? 0;
  const cap = budget?.cap ?? 110;
  const canGenerate =
    !generating && !budgetLoading && remaining >= 11;
  const nextReset = formatResetDate(budget?.next_reset_at);

  return (
    <div className="space-y-6" data-testid="wizard-step-generate">
      <div className="rounded-3xl border border-v2-border bg-v2-surface p-8 flex flex-col items-center gap-4 text-center">
        <div
          className="rounded-full p-4"
          style={{
            background: "rgba(167,139,250,0.14)",
            border: "1px solid rgba(167,139,250,0.42)",
          }}
        >
          <Sparkles className="w-6 h-6 text-v2-accent" />
        </div>
        <div className="max-w-md">
          <h3 className="text-v2-heading-sm font-semibold text-v2-text">
            Generate AI Draft
          </h3>
          <p className="mt-2 text-v2-body-sm text-v2-textMuted">
            Draft the 11 standard proposal sections from your wizard
            inputs. You will always review each section before use.
          </p>
        </div>

        {/* Budget indicator */}
        <div
          data-testid="wizard-generate-budget"
          className="rounded-2xl bg-v2-surfaceSubtle/60 border border-v2-border px-4 py-3 flex items-center gap-3"
        >
          {budgetLoading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin text-v2-textMuted" />
              <span className="text-v2-caption text-v2-textMuted">
                Checking your generation budget…
              </span>
            </>
          ) : (
            <>
              <Info className="w-3.5 h-3.5 text-v2-accent" />
              <span className="text-v2-caption text-v2-textMuted">
                Section-generations remaining:{" "}
                <strong className="text-v2-text">
                  {remaining} of {cap}
                </strong>
                {nextReset ? (
                  <>
                    {" "}
                    · Next reset:{" "}
                    <span className="text-v2-text">{nextReset}</span>
                  </>
                ) : null}
              </span>
            </>
          )}
        </div>

        {generating ? (
          <div
            data-testid="wizard-generate-in-progress"
            className="flex flex-col items-center gap-2"
          >
            <div className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-v2-body-sm font-semibold text-v2-accentInk bg-v2-accent/70">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating…
            </div>
            <p className="text-v2-caption text-v2-textMuted max-w-sm">
              Generating your proposal — this takes about a minute.
              Please keep this tab open.
            </p>
          </div>
        ) : (
          <button
            type="button"
            data-testid="wizard-generate-cta"
            onClick={handleGenerate}
            disabled={!canGenerate}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-v2-body-sm font-semibold text-v2-accentInk bg-v2-accent hover:bg-v2-accentHover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Generate AI Draft
          </button>
        )}

        {/* Insufficient-budget inline explanation (only shown when
            below threshold + not in a generating state). */}
        {!generating && !budgetLoading && remaining < 11 && (
          <p
            data-testid="wizard-generate-insufficient-budget"
            className="text-v2-caption text-amber-400 max-w-sm"
          >
            A full generation needs 11 section-generation slots; you
            have {remaining}.{" "}
            {nextReset ? `Next reset: ${nextReset}.` : ""}
          </p>
        )}

        <AIDisclosureBadge inline />
      </div>

      {banner && (
        <div
          data-testid={`wizard-generate-banner-${banner.kind}`}
          className={`rounded-2xl border p-4 flex items-start gap-3 ${
            banner.kind === "error"
              ? "border-red-500/40 bg-red-500/10 text-red-300"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          {banner.kind === "error" ? (
            <Ban className="w-4 h-4 flex-shrink-0 mt-0.5" />
          ) : (
            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-v2-body-sm font-semibold">{banner.title}</p>
            <p className="text-v2-caption mt-1">{banner.body}</p>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setBanner(null)}
            className="text-v2-textMuted hover:text-v2-text p-1 rounded"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
